import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Optional fallback renderer. Receives the captured error + a reset
   * function. When omitted, a default panel is shown. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

/**
 * Catches render-phase exceptions in the subtree below it.
 *
 * Without one of these, a single thrown error in any page component takes the
 * whole SPA white — React unmounts the entire root and shows nothing. The
 * boundary catches the throw, logs it (so the user can grab it from devtools),
 * and renders a fallback panel with a Reload button.
 *
 * Limitations React imposes on error boundaries (not bugs):
 *   - Async errors / promise rejections won't trigger it. Wrap those with
 *     `try/catch` and set component state manually.
 *   - Event-handler errors won't trigger it either — handlers fire outside
 *     the render cycle.
 *   - SSR errors aren't caught client-side.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console so the developer can read the full stack +
    // component tree. Browsers also display these in the Issues panel.
    console.error('ErrorBoundary caught render exception:', error, info)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset)
    }

    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        style={{
          maxWidth: 640,
          margin: '4rem auto',
          padding: '1.5rem',
          border: '1px solid var(--danger-border)',
          borderRadius: 8,
          background: 'var(--danger-soft)',
        }}
      >
        <h2 style={{ marginTop: 0, color: 'var(--danger)' }}>Something broke.</h2>
        <p style={{ marginBottom: '1rem' }}>
          The page hit an unexpected error. Reloading usually clears it; if it
          keeps happening, share the message below with the team.
        </p>
        <details style={{ marginBottom: '1rem' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
            Error details
          </summary>
          <pre
            data-testid="error-boundary-message"
            style={{
              marginTop: '0.5rem',
              padding: '0.75rem',
              background: 'var(--surface)',
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {error.message}
          </pre>
        </details>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            data-testid="error-boundary-reload"
            style={{
              padding: '0.5rem 1rem',
              background: '#d32029',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={this.reset}
            data-testid="error-boundary-retry"
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--surface)',
              border: '1px solid #ddd',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </div>
    )
  }
}
